import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';

/* ===================== Configuration MJF =====================
   Palier 2 : tasbih blanc, ajoute et retire automatiquement.
   Palier 3 : jilbab au choix dans le popup.
   Les six jilbabs offerts n'ont qu'une seule variante (pas de tailles) :
   le clic sur un modele selectionne directement la variante.
   Le palier 1 (45 EUR, livraison offerte) n'apparait pas ici : il est gere
   par le tarif de livraison Shopify, pas par un produit.
   ============================================================= */
const TIER2_CENTS = 6500; // 65€ - Tasbih blanc
const TIER3_CENTS = 13000; // 130€ - Jilbab au choix

const TASBIH_HANDLE = 'tasbih-blanc-99-perles-offert';

const JILBAB_HANDLES = [
  'jilbab-1-piece-rose-fuchsia-offert',
  'jilbab-1-piece-bleu-royal-offert',
  'jilbab-1-piece-camel-offert',
  'jilbab-1-piece-vert-foret-offert',
  'jilbab-1-piece-bordeaux-offert',
  'jilbab-1-piece-gris-clair-offert',
];

const SIZE_LABELS = {};

const DISMISS_KEY = 'mjf_gift_tier3_dismissed';
const OVERLAY_ID = 'mjf-gift-tier3-overlay';

/* =========================================================================
   CONTRÔLEUR DU POPUP
   -------------------------------------------------------------------------
   Simple <div> en position fixe (pas de <dialog> natif). Ouverture/fermeture
   = ajout/retrait d'une classe CSS. Auto-guérison : à chaque appel, on
   s'assure qu'il n'existe qu'une seule copie de l'overlay dans la page et
   qu'elle est bien un enfant direct de <body>, quel que soit l'endroit d'où
   elle a été rendue côté Liquid.
   ========================================================================= */

let popupEls = /** @type {Record<string, HTMLElement>} */ ({});
let popupBound = false;
let productsCache = /** @type {Record<string, any> | null} */ (null);
let selectedHandle = /** @type {string | null} */ (null);
let selectedVariantId = /** @type {number | null} */ (null);
let confirming = false;
let onConfirmAdd = /** @type {((handle: string, variantId: number) => Promise<void>) | null} */ (null);

/**
 * Retrouve (ou relocalise) l'overlay et câble ses événements une seule fois.
 * Appelée à chaque check pour rester robuste même si le rendu Liquid change.
 */
function ensurePopup() {
  const all = document.querySelectorAll('#' + OVERLAY_ID);

  if (all.length === 0) {
    popupEls = {};
    popupBound = false;
    return;
  }

  // S'il existe plusieurs copies (ex: rendue à la fois dans le tiroir et
  // dans theme.liquid), on garde celle actuellement ouverte, sinon la
  // première, et on supprime les autres.
  let keep = /** @type {HTMLElement} */ (all[0]);
  all.forEach((el) => {
    if (el.classList.contains('is-open')) keep = /** @type {HTMLElement} */ (el);
  });
  all.forEach((el) => {
    if (el !== keep) el.remove();
  });

  // On s'assure qu'elle est bien un enfant direct de <body>, pour ne
  // jamais être piégée dans un ancêtre transformé/animé (le tiroir panier).
  if (keep.parentElement !== document.body) {
    document.body.appendChild(keep);
  }

  if (popupBound && popupEls.overlay === keep) return;

  popupEls = {
    overlay: keep,
    products: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-products]')),
    sizes: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-sizes]')),
    confirm: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-confirm]')),
    close: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-close]')),
    decline: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-decline]')),
    error: /** @type {HTMLElement} */ (keep.querySelector('[data-gift-error]')),
  };

  if (
    !popupEls.products ||
    !popupEls.sizes ||
    !popupEls.confirm ||
    !popupEls.close ||
    !popupEls.decline ||
    !popupEls.error
  ) {
    return;
  }

  popupEls.close.addEventListener('click', () => closeGiftDialog(true));
  popupEls.decline.addEventListener('click', () => closeGiftDialog(true));
  popupEls.confirm.addEventListener('click', onConfirmClick);
  popupEls.overlay.addEventListener('click', (event) => {
    if (event.target === popupEls.overlay) closeGiftDialog(true);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isGiftDialogOpen()) closeGiftDialog(true);
  });

  popupBound = true;
}

function isGiftDialogOpen() {
  return !!popupEls.overlay && popupEls.overlay.classList.contains('is-open');
}

/**
 * @param {(handle: string, variantId: number) => Promise<void>} addGiftFn
 */
async function openGiftDialog(addGiftFn) {
  ensurePopup();
  if (!popupEls.overlay || isGiftDialogOpen()) return;

  onConfirmAdd = addGiftFn;

  if (!productsCache) {
    productsCache = {};
    await Promise.all(
      JILBAB_HANDLES.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            // @ts-ignore
            productsCache[handle] = await res.json();
          }
        } catch (error) {
          // ce produit sera simplement absent de la sélection
        }
      })
    );
  }

  renderProductCards();
  popupEls.overlay.classList.add('is-open');
  document.body.style.overflow = 'hidden';
}

/** @param {boolean} markDismissed */
function closeGiftDialog(markDismissed) {
  ensurePopup();
  if (popupEls.overlay) {
    popupEls.overlay.classList.remove('is-open');
  }
  document.body.style.overflow = '';
  if (markDismissed) {
    sessionStorage.setItem(DISMISS_KEY, '1');
  }
}

function renderProductCards() {
  const container = popupEls.products;
  container.innerHTML = '';

  JILBAB_HANDLES.forEach((handle) => {
    const product = productsCache?.[handle];
    if (!product) return;

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'mjf-gift-card';
    card.dataset.handle = handle;

    const img = document.createElement('img');
    img.className = 'mjf-gift-card__img';
    img.loading = 'lazy';
    img.alt = product.title;
    img.src = withWidth(product.featured_image, 200);

    const label = document.createElement('span');
    label.className = 'mjf-gift-card__label';
    label.textContent = product.title.replace(/\s*-\s*Offert$/i, '');

    card.appendChild(img);
    card.appendChild(label);
    card.addEventListener('click', () => selectProduct(handle));
    container.appendChild(card);
  });

  popupEls.sizes.hidden = true;
  popupEls.sizes.innerHTML = '';
  popupEls.confirm.classList.remove('is-ready');
  popupEls.error.hidden = true;
  selectedHandle = null;
  selectedVariantId = null;
}

/** @param {string} handle */
function selectProduct(handle) {
  selectedHandle = handle;
  selectedVariantId = null;
  popupEls.confirm.classList.remove('is-ready');
  popupEls.error.hidden = true;

  [...popupEls.products.children].forEach((card) => {
    card.classList.toggle('is-selected', /** @type {HTMLElement} */ (card).dataset.handle === handle);
  });

  const product = productsCache?.[handle];
  const sizesContainer = popupEls.sizes;
  const variants = product?.variants || [];

  // Produit sans declinaison (une seule variante "Default Title") : il n'y a
  // rien a choisir de plus, on valide directement la variante disponible.
  const monoVariante = variants.length <= 1 || variants.every((/** @type {any} */ v) => !v.option1 || v.option1 === 'Default Title');

  if (monoVariante) {
    sizesContainer.hidden = true;
    sizesContainer.innerHTML = '';
    const dispo = variants.find((/** @type {any} */ v) => v.available) || variants[0];
    if (dispo) {
      selectedVariantId = dispo.id;
      popupEls.confirm.classList.add('is-ready');
    }
    return;
  }

  sizesContainer.innerHTML = '<p class="mjf-gift-dialog__sizes-label">Taille</p>';
  sizesContainer.hidden = false;

  const row = document.createElement('div');
  row.className = 'mjf-gift-dialog__sizes-row';
  sizesContainer.appendChild(row);

  variants.forEach((/** @type {any} */ variant) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mjf-gift-size';
    btn.textContent = SIZE_LABELS[variant.option1] || variant.option1;
    btn.disabled = !variant.available;
    btn.addEventListener('click', () => {
      [...row.children].forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      selectedVariantId = variant.id;
      popupEls.confirm.classList.add('is-ready');
      popupEls.error.hidden = true;
    });
    row.appendChild(btn);
  });
}

async function onConfirmClick() {
  if (!selectedHandle || !selectedVariantId) {
    popupEls.error.hidden = false;
    return;
  }
  if (confirming) return;
  confirming = true;

  popupEls.error.hidden = true;
  const originalLabel = popupEls.confirm.textContent;
  popupEls.confirm.textContent = 'Ajout en cours...';

  if (onConfirmAdd) {
    await onConfirmAdd(selectedHandle, selectedVariantId);
  }

  popupEls.confirm.textContent = originalLabel;
  confirming = false;
  sessionStorage.removeItem(DISMISS_KEY);
  closeGiftDialog(false);
}

/**
 * @param {string} url
 * @param {number} width
 */
function withWidth(url, width) {
  if (!url) return url;
  return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
}

/* =========================================================================
   COMPOSANT DE SURVEILLANCE DU PANIER
   ========================================================================= */

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} reopenBtn
 * @extends {Component<Refs>}
 */
class MjfGiftThreshold extends Component {
  requiredRefs = ['reopenBtn'];

  #busy = false;
  #hasCheckedOnce = false;
  #debounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    ensurePopup();

    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleCheck);
    document.addEventListener(DiscountUpdateEvent.eventName, this.#scheduleCheck);
    window.addEventListener('pageshow', this.#scheduleCheck);

    this.refs.reopenBtn.addEventListener('click', () => openGiftDialog(this.#addGiftByHandle));

    this.#scheduleCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleCheck);
    document.removeEventListener(DiscountUpdateEvent.eventName, this.#scheduleCheck);
    window.removeEventListener('pageshow', this.#scheduleCheck);
  }

  #scheduleCheck = () => {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#check(), 250);
  };

  #sectionId() {
    const section = this.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  /**
   * @param {string} handle
   * @param {number} [variantId]
   */
  #addGiftByHandle = async (handle, variantId) => {
    let id = variantId;
    if (!id) {
      const product = await fetch(`/products/${handle}.js`).then((r) => r.json());
      const available = product.variants.find((/** @type {any} */ v) => v.available);
      id = available ? available.id : product.variants[0]?.id;
    }
    if (!id) return;

    const sectionId = this.#sectionId();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections: sectionId ? [sectionId] : [],
      }),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();
      if (sectionId && data.sections && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    } catch (error) {
      // no-op, on retentera au prochain check
    }

    this.#scheduleCheck();
  };

  /**
   * @param {string} key
   * @param {number} quantity
   */
  async #setLineQuantity(key, quantity) {
    const sectionId = this.#sectionId();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sectionId ? [sectionId] : [],
      }),
    });

    try {
      const response = await fetch(Theme.routes.cart_change_url, config);
      const data = await response.json();
      if (sectionId && data.sections && data.sections[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    } catch (error) {
      // no-op, on retentera au prochain check
    }

    this.#scheduleCheck();
  }

  async #check() {
    if (this.#busy) return;
    this.#busy = true;
    const isFirstCheck = !this.#hasCheckedOnce;
    this.#hasCheckedOnce = true;

    try {
      ensurePopup();

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const total = cart.total_price;
      const tasbihItem = cart.items.find((/** @type {any} */ item) => item.handle === TASBIH_HANDLE);
      const jilbabLine = cart.items.find((/** @type {any} */ item) => JILBAB_HANDLES.includes(item.handle));

      // --- Garde-fou absolu : sous 130€, le popup ne doit JAMAIS rester ouvert ---
      if (total < TIER3_CENTS && isGiftDialogOpen()) {
        closeGiftDialog(false);
      }

      // --- Garde-fou : un cadeau ne doit jamais avoir une quantité > 1 ---
      if (tasbihItem && tasbihItem.quantity > 1) {
        this.#busy = false;
        await this.#setLineQuantity(tasbihItem.key, 1);
        return;
      }
      if (jilbabLine && jilbabLine.quantity > 1) {
        this.#busy = false;
        await this.#setLineQuantity(jilbabLine.key, 1);
        return;
      }

      // --- Palier 2 : Tasbih blanc (65€) ---
      if (total >= TIER2_CENTS && !tasbihItem) {
        this.#busy = false;
        await this.#addGiftByHandle(TASBIH_HANDLE);
        return;
      }
      if (total < TIER2_CENTS && tasbihItem) {
        this.#busy = false;
        await this.#setLineQuantity(tasbihItem.key, 0);
        return;
      }

      // --- Palier 3 : Jilbab au choix (130€) ---
      if (total < TIER3_CENTS) {
        if (jilbabLine) {
          this.#busy = false;
          await this.#setLineQuantity(jilbabLine.key, 0);
          return;
        }
        sessionStorage.removeItem(DISMISS_KEY);
        this.refs.reopenBtn.hidden = true;
      } else {
        if (jilbabLine) {
          this.refs.reopenBtn.hidden = true;
        } else {
          this.refs.reopenBtn.hidden = false;
          if (!isFirstCheck && sessionStorage.getItem(DISMISS_KEY) !== '1' && !isGiftDialogOpen()) {
            await openGiftDialog(this.#addGiftByHandle);
          }
        }
      }
    } catch (error) {
      // Échec silencieux : le prochain déclencheur retentera
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('mjf-gift-threshold')) {
  customElements.define('mjf-gift-threshold', MjfGiftThreshold);
}